// Copied from platform-app components/pos/layout/MoreLauncherPreview.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React from 'react';
import { BUTTON_REGISTRY, mergeButton, type ButtonOverride } from '../../../config/buttonRegistry';
import { resolveButtonLook } from '../../../config/buttonTheme';
import { getIcon } from '../../../utils/iconRegistry';
import { Pin, Search, Palette } from 'lucide-react';
import { PREVIEW_PALETTE, type PreviewTheme } from './previewPalette';

export interface MoreLauncherPreviewProps {
  availableIds: string[];
  pinnedIds: string[];
  deactivatedIds: string[];
  customizations: Record<string, ButtonOverride>;
  onTogglePin: (id: string) => void;
  onCustomize: (id: string) => void;
  theme?: PreviewTheme;
}

const GROUP_ORDER: { key: string; label: string }[] = [
  { key: 'sales', label: 'Sales' },
  { key: 'register', label: 'Register' },
  { key: 'employee', label: 'Employee' },
  { key: 'admin', label: 'Admin' },
];

export default function MoreLauncherPreview({ availableIds, pinnedIds, deactivatedIds, customizations, onTogglePin, onCustomize, theme = 'light' }: MoreLauncherPreviewProps) {
  const p = PREVIEW_PALETTE[theme];
  const pinnedSet = new Set(pinnedIds);
  const deactSet = new Set(deactivatedIds);
  const groups = [...GROUP_ORDER.map((g) => g.key), 'other'];

  const idsForGroup = (key: string) =>
    availableIds.filter((id) => {
      const g = BUTTON_REGISTRY[id]?.group;
      if (!g) return key === 'other';
      return key === 'other' ? !GROUP_ORDER.some((x) => x.key === g) : g === key;
    });

  return (
    <div className="overflow-hidden rounded-2xl border border-stone-200">
      <div className="flex items-center gap-2.5 px-3.5 py-2.5" style={{ background: p.head, borderBottom: `1px solid ${p.headBor}`, color: p.inputTx }}>
        <Search size={16} />
        <div className="flex h-[34px] flex-1 items-center rounded-lg px-3 text-[13px]" style={{ background: p.input, color: p.inputTx }}>Search actions…</div>
      </div>
      <div className="px-[18px] pb-[18px] pt-0.5" style={{ background: p.body }}>
        {groups.map((key) => {
          const ids = idsForGroup(key);
          if (ids.length === 0) return null;
          const label = GROUP_ORDER.find((g) => g.key === key)?.label ?? 'Other';
          return (
            <div key={key}>
              <div className="mx-0.5 mb-2 mt-4 text-[10.5px] font-bold uppercase tracking-wider" style={{ color: p.mu }}>{label}</div>
              <div className="flex flex-wrap gap-x-3.5 gap-y-3">
                {ids.map((id) => {
                  const merged = mergeButton(id, customizations[id]);
                  if (!merged) return null;
                  const Icon = getIcon(merged.icon);
                  const isPinned = pinnedSet.has(id);
                  const isDeact = deactSet.has(id);
                  const look = resolveButtonLook(id, customizations[id], theme);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => (isDeact ? onCustomize(id) : onTogglePin(id))}
                      className={`group relative flex w-[76px] flex-col items-center gap-1.5 rounded-[10px] px-0 pb-[5px] pt-1.5 transition-colors hover:bg-black/5 ${isDeact ? 'opacity-50' : ''}`}
                      title={isDeact ? 'Deactivated — customize to reactivate' : isPinned ? 'Tap to unpin from rail' : 'Tap to pin to rail'}
                    >
                      {isPinned && !isDeact && (
                        <span className="absolute right-[5px] top-px z-[1] flex h-[18px] w-[18px] items-center justify-center rounded-[5px] shadow" style={{ background: p.badgeBg, color: p.badgeTx }}>
                          <Pin size={11} color={p.badgeTx} />
                        </span>
                      )}
                      <span className="flex h-[56px] w-[56px] items-center justify-center rounded-xl" style={look.style}>
                        {Icon && <Icon size={22} color={look.fg} />}
                      </span>
                      <span className={`max-w-[74px] truncate text-[11px] font-semibold ${isDeact ? 'line-through' : ''}`} style={{ color: p.mu }}>{merged.label}</span>
                      <span
                        role="button"
                        onClick={(e) => { e.stopPropagation(); onCustomize(id); }}
                        className="absolute bottom-[22px] right-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-[5px] opacity-0 transition-opacity hover:bg-black/10 group-hover:opacity-100"
                        style={{ color: p.tx }}
                        title="Customize"
                      >
                        <Palette size={11} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
